import { FC, useEffect, useRef, useState } from "react";
import GridSelectorInput from "../../../../../components/controls/GridSelectorInput";
import GridWrapper from "../../../../../components/controls/GridWrapper";
import { NumberInput, TextAreaInput, TextInput } from "../../../../../components/controls/inputs";
import { BaseModalWindow } from "../../../../../components/modalWindows/BaseModalWindow";
import modalStyles from "../../../../../components/modalWindows/styles/BaseModalWindow.module.scss";
import { ScalingRatioSelectorModal } from "../../../../Dictionaries/ScalingRatio";
import styles from "../../styles/ActDisagregationCreator.module.scss";
import { IEntitySimpleDTO } from "../../../../../libs/coreapi-dto/@types/common";
import { useForm } from "../../../../../system/hooks/useForm";
import { useTranslation } from "react-i18next";
import { ILotDTO } from "../../../../../libs/coreapi-dto/accounting/lot";
import { useAppContext } from "../../../../../system/providers/appContextProvider";
import { ScalingRatioDataProvider } from "../../../../../Services/DataProviders/ScalingRatioDataProvider";
import { IActDisagregationItemEditDTO } from "../../../../../libs/coreapi-dto/documents/actDisagregationItem";
import { IPricingDocumentItemDTO } from "./../../../../../libs/coreapi-dto/dirs/pricingInvoice";
import { PricingDisaggregationDataProvider } from "../../../../../Services/DataProviders/PricingDisaggregationDataProvider";
import { GoodsDataProvider } from "../../../../../Services/DataProviders/GoodsDataProvider";
import { ICreateItem } from "../ActDisagregationUpdateForm";
import { DateTime } from "luxon";
import { IScalingRatioDTO, IScalingRatioViewDTO } from "../../../../../libs/coreapi-dto/dirs/scalingRatio";
import { KizBoxDataProvider } from "../../../../../Services/DataProviders/KizBoxDataProvider";
import { IKizBoxDTO } from "../../../../../libs/coreapi-dto/dirs/kiz";
import { roundMoneyFormat } from "../../../../../system/functions/round";
import renderGlobalAlert from "../../../../../system/hooks/useGlobalAlert";
import { DecimalInputV2, formatNumberWithTwoDecimalPlaces } from "../../../../../components/controls/inputs/BaseInput";
import { ContractorDataProvider } from "../../../../../Services/DataProviders/ContractorDataProvider";
import { IContractorDTO } from "../../../../../libs/coreapi-dto/dirs/contractor";
import { promisifyMethod } from "../../../../../system/functions/promisifyMethod";
import { ProgressActionSpinner } from "../../../../../components/progressActionSpinner/ProgressActionSpinner";

interface IValidator {
    retailPriceWithVat: number;
    scalingRatioTo: IEntitySimpleDTO;
}
interface IActDisagregationCreatorItemModal {
    ok: (item: ICreateItem, postProcess?: () => void, fallback?: () => void) => void;
    cancel: () => void;
    itemLot: ILotDTO;
    itemEdit: IActDisagregationItemEditDTO;
    pricingModel?: IEntitySimpleDTO;
    partlyMode: boolean
    editMode: boolean
    autoConfirm?: boolean;
}
interface IScalingRatioTo {
    scalingRatioEntityTo: IEntitySimpleDTO;
    scalingRatioToNumerator: number;
    scalingRatioToDenominator: number;
}

export const ActDisagregationUpdateItemModal: FC<IActDisagregationCreatorItemModal> = (props) => {
    const appCtx = useAppContext();

    const pricingDP = new PricingDisaggregationDataProvider(appCtx.coreApiService);
    const scalingRatioDP = new ScalingRatioDataProvider(appCtx.coreApiService);
    const goodsDP = new GoodsDataProvider(appCtx.coreApiService);
    const kizBoxDP = new KizBoxDataProvider(appCtx.coreApiService);
    const contractorDP = new ContractorDataProvider(appCtx.coreApiService);

    const [data, setData] = useState<IActDisagregationItemEditDTO>({
        ...props.itemEdit,
        retailCostInfo: {
            ...props.itemEdit.retailCostInfo,
            priceIncVat: Number(formatNumberWithTwoDecimalPlaces(props.itemEdit.retailCostInfo.priceIncVat).replace(/\s/g, '')),//Функция которая округляет к большему
        }
    });

    const [scalingRatioTo, setScalingRatioTo] = useState<IScalingRatioTo>({
        scalingRatioEntityTo: {} as IEntitySimpleDTO,
        scalingRatioToNumerator: 0,
        scalingRatioToDenominator: 0,
    });

    const [quantityAfter, setQuantityAfter] = useState<number>(0);
    const [quantityLotsAfter, setQuantityLotsAfter] = useState<number>(0);
    const [uniqueBoxesInPalette, setUniqueBoxesInPalette] = useState<boolean>(true);
    const [initializationComplete, setInitializationComplete] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState<boolean>(props.autoConfirm || false);

    const [isCalculated, setIsCalculated] = useState<boolean>(true);
    const [isGnvls, setIsGnvls] = useState<boolean>(false);

    const { t } = useTranslation();
    const errorsT = (value: string) => t("errors." + value);
    const baseT = (value: string) => t("modals.actDisagregationUpdateItem." + value);

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            retailPriceWithVat: {
                custom: {
                    isValid: () => uniqueBoxesInPalette ? data.retailCostInfo?.priceIncVat !== undefined && data.retailCostInfo?.priceIncVat > 0 : true,
                    message: errorsT("onlyPositiveValue"),
                },
            },
            scalingRatioTo: {
                required: {
                    value: uniqueBoxesInPalette ? (scalingRatioTo?.scalingRatioEntityTo?.displayName ? false : true) : false,
                    message: errorsT("required"),
                },
            },
        },
    });

    useEffect(() => {
        if (props.itemLot) {
            goodsDP.getById(props.itemLot.goods.idGlobal, (e) => {
                setIsGnvls(e.gnvls.important ?? false);
            });
        }

        const setScalingRatioToWithNumerator = (numerator: number, postProcess?: () => void) => {
            scalingRatioDP.overrideGetView(
                props.itemLot.goods.idGlobal,
                {
                    pageNumber: 1,
                    numberPerPage: 1,
                    columnFilters: [
                        { name: "numerator", operator: "Eq", value: numerator.toString() } as IViewFilter, 
                        { name: "denominator", operator: "Eq", value: "1" } as IViewFilter
                    ],
                } as IViewBody,
                (scalingRatios: IScalingRatioViewDTO[]) => {
                    if (scalingRatios.length > 0) {
                        scalingRatioDP.overrideGetById(props.itemLot.goods.idGlobal, scalingRatios[0].idGlobal, (sr) => {
                            setScalingRatioTo({
                                scalingRatioEntityTo: { idGlobal: sr.idGlobal, displayName: sr.displayName } as IEntitySimpleDTO,
                                scalingRatioToNumerator: sr.numerator,
                                scalingRatioToDenominator: sr.denominator,
                            });

                            postProcess?.();
                        },
                            (err: string) => {
                                renderGlobalAlert({
                                    variant: "error",
                                    title: err,
                                    statusCode: 0
                                })

                                setShowSpinner(false)
                            });
                    } else {
                        if (props.itemLot.scalingRatio) {
                            scalingRatioDP.overrideGetById(props.itemLot.goods.idGlobal, props.itemLot.scalingRatio.idGlobal, (sr: IScalingRatioDTO) => {
                                scalingRatioDP.overrideCreate(props.itemLot.goods.idGlobal, { numerator: numerator, denominator: 1, unit: sr.unit } as IScalingRatioDTO,
                                    (idScalingRatioGlobal: string) => {
                                        setScalingRatioTo({
                                            scalingRatioEntityTo: { idGlobal: idScalingRatioGlobal, displayName: `${numerator}/1 ${sr.unit.displayName}` } as IEntitySimpleDTO,
                                            scalingRatioToNumerator: numerator,
                                            scalingRatioToDenominator: 1,
                                        });

                                        postProcess?.();
                                    },
                                    () => {
                                        setShowSpinner(false)
                                    });
                            },
                                (err: string) => {
                                    renderGlobalAlert({
                                        variant: "error",
                                        title: err,
                                        statusCode: 0
                                    })

                                    setShowSpinner(false)
                                });
                        }
                    }
                },
                undefined,
                () => {
                    setShowSpinner(false);
                }
            );
        };

        const setScalingRatioWithChildren = (packedBox: IKizBoxDTO) => {
            kizBoxDP.findChildren(packedBox.idGlobal, (packedBoxes: IKizBoxDTO[]) => {
                if (packedBoxes.length > 0) {

                    // Для коробов с равным количеством выполняется стандартное разбиение
                    const uniqueBoxes = [...new Set(packedBoxes.map(item => item.quantity))]
                    if (uniqueBoxes.length === 1) {
                        setScalingRatioToWithNumerator(packedBox.quantity / packedBoxes.length, () => {
                            setQuantityAfter(packedBoxes.length);
                            setUniqueBoxesInPalette(true);
                            handleInitializationComplete();
                        });
                    } else {
                        // Неоднородные короба
                        setQuantityAfter(packedBoxes.length);
                        setQuantityLotsAfter(uniqueBoxes.length);
                        setUniqueBoxesInPalette(false);
                        setScalingRatioTo({
                            ...scalingRatioTo,
                            scalingRatioEntityTo: {
                                ...scalingRatioTo.scalingRatioEntityTo,
                                displayName: `${uniqueBoxes.length} × третичн. уп.`
                            }
                        });
                        handleInitializationComplete();
                    }
                } else {
                    setShowSpinner(false);
                    renderGlobalAlert({ variant: "warning", statusCode: 200, title: baseT("paletteEntriesNotDownloadedFromMDLP"), isSound: true });
                    props.cancel();
                }
            },
                () => {
                    setShowSpinner(false);
                });
        };

        const handleInitializationComplete = () => {
            setInitializationComplete(true);
            if (props.partlyMode && !props.editMode && scalingRatioTo.scalingRatioToNumerator > 0) {
                calculated(roundMoneyFormat(props.itemEdit.retailCostInfo.priceIncVat) * scalingRatioTo.scalingRatioToNumerator);
            }
        };

        if (props.partlyMode) {
            // Предполагается одна палета на один товар (sscc) взятый из ПН
            let packedBox: IKizBoxDTO | null = props.itemEdit.palettes && props.itemEdit.palettes.length > 0 ? props.itemEdit.palettes[0] : null;
            if (packedBox !== null) {
                setScalingRatioWithChildren(packedBox)
            } else {
                renderGlobalAlert({ 
                    variant: "warning", 
                    statusCode: 200, 
                    title: "Данный короб не является паллетой", 
                    detail: "Для разагрегации короба отключите опцию \"Разагрегация паллеты до короба\"",
                    isSound: true
                });
                props.cancel();
            }
        } else {
            setScalingRatioToWithNumerator(1, () => {
                setUniqueBoxesInPalette(true);
                handleInitializationComplete();
            });
        }
    }, [props.itemLot.goods.idGlobal]);

    useEffect(() => {
        if (props.autoConfirm && initializationComplete && scalingRatioTo.scalingRatioEntityTo !== {} as IEntitySimpleDTO && isCalculated) {
            const timer = setTimeout(async () => {
                try {
                    if (validateUnitPriceIncVatDelta() && isValid()) {
                        props.ok(await returnCreateItem(), () => setShowSpinner(false), () => setShowSpinner(false));
                    } else {
                        setShowSpinner(false);
                    }
                } catch (error) {
                    console.error(error);
                    setShowSpinner(false);
                }
            }, 30);
            
            return () => clearTimeout(timer);
        }
    }, [props.autoConfirm, scalingRatioTo, isCalculated, initializationComplete]);
    
    const calculated = (retailPriceWithVat: number, idScalingRatio?: string) => {
        setIsCalculated(false);
        const calcItem = ({
            quantity: (props.partlyMode ) ? quantityAfter : data.quantity,

            supplierPriceExcludingVat: data.supplierCostInfo.price ?? 0,
            supplierVatRatePercentage: data.supplierCostInfo.vat ?? 0,
            supplierPriceWithVat: data.supplierCostInfo.priceIncVat ?? 0,
            supplierAmountExcludingVat: data.supplierCostInfo.sum ?? 0,
            supplierVatAmount: data.supplierCostInfo.vatSum ?? 0,
            supplierAmountWithVat: data.supplierCostInfo.sumIncVat ?? 0,

            percentageOfMarkupRetail: data.productMargin ?? 0,
            percentageOfVatRateRetail: data.retailCostInfo.vat ?? 0,
            retailPrice: data.retailCostInfo.price ?? 0,
            retailPriceWithVat,
            retailAmount: data.retailCostInfo.sum ?? 0,
            retailAmountWithVat: data.retailCostInfo.sumIncVat ?? 0,
            vatAmountRetail: data.retailCostInfo.vatSum ?? 0,

            numerator: scalingRatioTo.scalingRatioToNumerator ?? 0,
            denominator: scalingRatioTo.scalingRatioToDenominator ?? 0,
            eventType: "RetailPriceWithVatChanged",
        } as IPricingDocumentItemDTO);
        setData({ ...data, retailCostInfo: { ...data.retailCostInfo, priceIncVat: calcItem.retailPriceWithVat } });

        if (!props.pricingModel || (props.partlyMode && scalingRatioTo.scalingRatioToNumerator === 0))
            return;
        
        pricingDP.calculateItem(props.pricingModel?.idGlobal as string, calcItem, (pricingDocumentItem: IPricingDocumentItemDTO) => {
            setData({
                ...data,
                retailCostInfo: {
                    adprice: pricingDocumentItem.percentageOfMarkupRetail,
                    vatPrice: pricingDocumentItem.retailVatPerUnit,
                    vat: pricingDocumentItem.percentageOfVatRateRetail,
                    vatSum: pricingDocumentItem.vatAmountRetail,
                    sumIncVat: pricingDocumentItem.retailAmountWithVat,
                    sum: pricingDocumentItem.retailAmount,
                    price: pricingDocumentItem.retailPrice,
                    priceIncVat: pricingDocumentItem.retailPriceWithVat,
                },
                supplierCostInfo: {
                    adprice: pricingDocumentItem.supplierMarkupPercentage,
                    vatPrice: pricingDocumentItem.supplierVatPerUnit,
                    vat: pricingDocumentItem.supplierVatRatePercentage,
                    vatSum: pricingDocumentItem.supplierVatAmount,
                    sumIncVat: pricingDocumentItem.supplierAmountWithVat,
                    sum: pricingDocumentItem.supplierAmountExcludingVat,
                    price: pricingDocumentItem.supplierPriceExcludingVat,
                    priceIncVat: pricingDocumentItem.supplierPriceWithVat,
                },
                productMargin: pricingDocumentItem.retailUnitMargin,
                quantityFrom: 1,
                quantity: props.itemEdit.quantity,
                idScalingRatioToGlobal: idScalingRatio ?? data.idScalingRatioToGlobal,
            });
            setIsCalculated(true);
        });
    };

    const returnCreateItem = async (): Promise<ICreateItem> => {
        const itemContractor: IContractorDTO = await promisifyMethod<IContractorDTO, typeof contractorDP>(
            contractorDP,
            contractorDP.getById,
            props.itemLot.contractor.idGlobal
        );

        return {
            item: {
                ...data,
                idScalingRatioToGlobal: props.partlyMode ? scalingRatioTo.scalingRatioEntityTo.idGlobal : data.idScalingRatioToGlobal, 
                quantity: (props.partlyMode) ? quantityAfter : data.quantity, 
                dateCreated: DateTime.now() 
            },
            displayItem: {
                idGlobal: data.idGlobal,
                idLotFromGlobal: data.idLotFromGlobal,
                goodsName: props.itemLot.goods.displayName,
                quantity: (props.partlyMode) ? quantityAfter : data.quantity,
                goodsCode: props.itemLot.goodsCode,
                producer: props.itemLot.producer.displayName,
                scalingRatioFrom: props.itemLot.scalingRatio.displayName,
                scalingRatioTo: scalingRatioTo.scalingRatioEntityTo.displayName,
                supplierPrice: data.supplierCostInfo.price,
                supplierSum: data.supplierCostInfo.sum,
                quantityFrom: data.quantityFrom,
                isGnvls: isGnvls,
                isKiz: props.itemLot.isKizBox ?? false,
                supplier: props.itemLot.supplier,
                retailPriceIncVat:  data.retailCostInfo?.priceIncVat,
                lotRetailPriceIncVat: data.retailCostInfo.sumIncVat,
                dateCreated: DateTime.now()
            },
            trusted: itemContractor.attributesInfo.trustedSupplier
        };
    };

    useEffect(() => {
        if (props.partlyMode && !props.editMode && scalingRatioTo.scalingRatioToNumerator > 0 && initializationComplete) {
            calculated(roundMoneyFormat(props.itemEdit.retailCostInfo.priceIncVat) * scalingRatioTo.scalingRatioToNumerator);
        }

    }, [scalingRatioTo.scalingRatioToNumerator, initializationComplete])
   
    function validateUnitPriceIncVatDelta() {

        // Для неоднородного состава палеты отключаем валидацию
        if (!uniqueBoxesInPalette) return true;

        const quantity: number = props.partlyMode ? props.itemEdit.kizBoxCount : props.itemEdit.quantity

        const newNumber = data.retailCostInfo.priceIncVat * quantity; //Цена после разагрегации.
        const oldNumber = data.retailCostInfo.sumIncVat; //Исходная цена.

        const result = Math.abs((newNumber / oldNumber - 1) * 100);

        //Проверка на отрицательные числа и результаты больше 1 процента.
        if (result > 1) {
            renderGlobalAlert({ variant: "exception", statusCode: 500, title: baseT("pricesDiff") + `: ${oldNumber} → ${newNumber}`});
            return false;
        }

        return true
    }

    if (showSpinner) {
        return (
            <ProgressActionSpinner
                loaderText={`Создание позиции`}
            />
        );
    }
    return (
        <BaseModalWindow
            header={`${baseT("header")} ${baseT(props.partlyMode ? "palette" : "box")}`} //"Создание позиции"
            ok={{
                onClick: async () => {
                    if(validateUnitPriceIncVatDelta()) {
                        if (isCalculated && isValid()) {
                            props.ok(await returnCreateItem(), () => setShowSpinner(false), () => setShowSpinner(false));
                        }
                    }
                },
                title: "ОК",
            }}
            cancel={{
                onClick: () => {
                    props.cancel();
                },
                title: baseT("cancel") //"Отменить",
            }}
            modalWindowClassName={modalStyles.modalWindowConfirm}
            footerStyle="confirm"
        >
            <TextAreaInput label={baseT("goodsName")}/*"Товар"*/ className={styles.field_GoodsName} value={props.itemLot.goods.displayName} disabled />
            <GridWrapper cols={2}>
                <TextInput label={baseT("lotName")}/*"Партия"*/ disabled value={props.itemLot.docNum} />
                <TextInput label={props.partlyMode ? "Единица измерения паллеты" : baseT("scalingRatioFrom")}/*"Единица измерения"*/ value={props.itemLot.scalingRatio.displayName} disabled />
            </GridWrapper>
            <GridWrapper cols={3}>
                <NumberInput label={props.partlyMode ? "Количество паллет на складе" : baseT("quantityToOnStore")}/*"Количество единиц на складе"*/ disabled value={props.itemLot.quantityRem + props.itemLot.quantityRes} />
                <NumberInput label={props.partlyMode ? "Количество паллет после разагрегации" : baseT("quantityFromOnStore")}/*"Количество исходных единиц"*/ disabled value={props.partlyMode ? 0 : 1} />
                <DecimalInputV2 label={props.partlyMode ? "Исходная цена паллеты" : baseT("priceBase")}/*"Исходная цена"*/ disabled value={data.quantityFrom > 0 ? data.retailCostInfo.sumIncVat /*data?.retailCostInfoFrom?.sumIncVat*/ : 0} />
            </GridWrapper>
            <GridWrapper cols={uniqueBoxesInPalette ? 3 : 2}>
                {uniqueBoxesInPalette && <GridSelectorInput
                    selectorModalJsx={ScalingRatioSelectorModal}
                    id={"inputAccompanyingSelectionGoods"}
                    label={props.partlyMode ? "Единица измерения паллеты" : baseT("scalingRatioTo")}//"Единица разагрегации"
                    disabled
                    idGlobal={props.itemLot.goods.idGlobal}
                    selectedEntity={scalingRatioTo.scalingRatioEntityTo}
                    error={errors.scalingRatioTo}
                    onFocus={() => setErrors({ ...errors, scalingRatioTo: undefined })}
                />}
                <NumberInput
                    label={baseT(props.partlyMode ? "quantityAfterBoxes" : "quantityAfter")}/*"Количество после разагрегации"*/
                    disabled
                    value={props.partlyMode ? quantityAfter : props.itemEdit.quantity}
                />
                {uniqueBoxesInPalette && <DecimalInputV2
                    disabled={!isCalculated}
                    required
                    label={props.partlyMode ? "Цена короба" : baseT("priceAfter")}//"Цена после разагрегации"
                    value={data.retailCostInfo.priceIncVat ?? 0}
                    onChange={(value) => {
                        setData((prev) => ({
                            ...prev,
                            retailCostInfo: {
                                ...prev.retailCostInfo,
                                priceIncVat: value
                            }
                        }))
                    }}
                    error={errors.retailPriceWithVat}
                    onFocus={() => setErrors({ ...errors, retailPriceWithVat: undefined })}
                />}
                {!uniqueBoxesInPalette && <NumberInput
                    disabled
                    value={quantityLotsAfter}
                    label={"Количество партий после разагрегации"}
                />}
            </GridWrapper>
        </BaseModalWindow>
        
    );
};
